/**
 * SchemaViewer — read/write console for the discovered DB/ERP schema.
 *
 * Layout (Jul 2026 redesign — the old 260px/max-h-[420px] two-column grid was
 * cramped and unreadable):
 *   - Left rail (300px, tall): searchable table list, pencil rows (no fill,
 *     inline row-count + AI glyph).
 *   - Main pane: selected table — inline meta (MetaRow), a "Anteprima dati"
 *     toggle that fetches 20 live sample ROWS, an editable table description,
 *     and the columns rendered as a DENSE list. Each column shows its rich
 *     metadata (type, PK/FK, cardinality, nullability) already in the schema
 *     payload, an inline description editor, and a "Dati" peek that fetches
 *     ~20 DISTINCT live values so the user can see what's actually in the
 *     field before annotating it.
 *
 * Profiling is ORG-SHARED: the BE resolves every read/write to the tenant's
 * profile owner, so a description edited by one member is seen by all
 * ("cambia uno vale per tutti"). We surface that with a one-line note.
 *
 * "Salva" batches table + all edited columns into ONE PUT (BE contract).
 */

import { msg } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Columns3,
  Database,
  Eye,
  Hash,
  KeyRound,
  Link2,
  Loader2,
  Search,
  Sparkles,
  Users,
} from "lucide-react";
import * as React from "react";

import type { ColumnSchemaResponse, SchemaResponse, TableSchemaResponse } from "@ando/core/api";
import { Button, HighlightNote, MetaItem, MetaRow, Skeleton, cn, useDebounce } from "@ando/ui";

import { enterpriseSchemaKey } from "../../hooks/enterpriseKeys";
import { useProfilingStatus } from "../../hooks/useProfilingStatus";
import { useSampleColumn, useSampleRows } from "../../hooks/useSampleData";
import { useSchema } from "../../hooks/useSchema";
import { useUpdateSchemaDescription } from "../../hooks/useUpdateSchemaDescription";
import { InlineErrorBanner } from "../InlineErrorBanner";
import { BusinessContextBanner } from "./BusinessContextBanner";

export interface SchemaViewerProps {
  readonly connId: string;
  /** Hide the empty-state CTA — caller already has a top-level "Avvia scoperta". */
  readonly onStartDiscovery?: () => void;
}

const SEARCH_DEBOUNCE_MS = 150;

function isAiDescribed(description: string | undefined): boolean {
  if (!description) return false;
  return description.trim().length > 0;
}

function matchesQuery(table: TableSchemaResponse, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return table.name.toLowerCase().includes(q) || table.description.toLowerCase().includes(q);
}

/**
 * Coerce a raw sample value to a display string. ``maxLen`` caps the length —
 * the compact per-column value chips use the default 48, while the row preview
 * passes a high cap so the FULL value survives to the cell (CSS truncates the
 * display to the resizable column width; the tooltip + click-to-copy reveal it).
 */
function formatSampleValue(v: unknown, maxLen = 48): { text: string; isNull: boolean } {
  if (v === null || v === undefined) return { text: "NULL", isNull: true };
  const s = typeof v === "string" ? v : String(v);
  return { text: s.length > maxLen ? `${s.slice(0, maxLen)}…` : s, isNull: false };
}

export const SchemaViewer: React.FC<SchemaViewerProps> = ({ connId, onStartDiscovery }) => {
  const { i18n } = useLingui();
  const schemaQuery = useSchema(connId);
  const profilingQuery = useProfilingStatus(connId);
  const isSyncing = Boolean(
    profilingQuery.data?.is_discovering ||
      profilingQuery.data?.is_profiling ||
      profilingQuery.data?.status === "pending" ||
      profilingQuery.data?.status === "in_progress",
  );

  // When background sync flips running → done, force one schema refetch.
  const queryClient = useQueryClient();
  const wasSyncingRef = React.useRef(isSyncing);
  React.useEffect(() => {
    if (wasSyncingRef.current && !isSyncing) {
      void queryClient.invalidateQueries({ queryKey: enterpriseSchemaKey(connId) });
    }
    wasSyncingRef.current = isSyncing;
  }, [isSyncing, connId, queryClient]);

  const [search, setSearch] = React.useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [selectedTable, setSelectedTable] = React.useState<string | null>(null);

  const data: SchemaResponse | undefined = schemaQuery.data;

  const filteredTables = React.useMemo(() => {
    if (!data) return [] as readonly TableSchemaResponse[];
    return data.tables.filter((t) => matchesQuery(t, debouncedSearch));
  }, [data, debouncedSearch]);

  React.useEffect(() => {
    if (!selectedTable && filteredTables.length > 0) {
      setSelectedTable(filteredTables[0]!.name);
    }
  }, [filteredTables, selectedTable]);

  const currentTable = React.useMemo<TableSchemaResponse | null>(() => {
    if (!data || !selectedTable) return null;
    return data.tables.find((t) => t.name === selectedTable) ?? null;
  }, [data, selectedTable]);

  if (schemaQuery.isLoading) {
    return <SchemaSkeleton />;
  }
  if (schemaQuery.isError) {
    return (
      <InlineErrorBanner
        message={i18n._(msg`Impossibile caricare lo schema. Controlla la connessione e riprova.`)}
      />
    );
  }
  if (!data || data.tables.length === 0) {
    if (isSyncing) {
      return <SchemaSyncInProgress />;
    }
    return <SchemaEmptyState onStartDiscovery={onStartDiscovery} />;
  }

  return (
    <div className="flex flex-col gap-5">
      {/* AI-generated business-context overview */}
      <BusinessContextBanner connId={connId} />

      {/* Org-sharing note — the profiling is tenant-wide. */}
      <HighlightNote tone="neutral" icon={<Users className="h-3.5 w-3.5" aria-hidden />}>
        <Trans>
          Le descrizioni sono condivise con tutto il team: una modifica vale per tutti gli utenti
          della tua organizzazione.
        </Trans>
      </HighlightNote>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        {/* ── Left rail: search + table list (tall, on cream) ─────────── */}
        <aside className="flex min-w-0 flex-col gap-2.5">
          <div className="relative">
            <Search
              className="-translate-y-1/2 absolute top-1/2 left-2.5 h-4 w-4 text-ink-3"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={i18n._(msg`Cerca tabella…`)}
              aria-label={i18n._(msg`Cerca tabella`)}
              className="w-full rounded-md border border-hairline bg-transparent py-2 pr-2.5 pl-8 text-[13px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
            />
          </div>
          <p className="px-0.5 text-[11px] text-ink-4">
            <Plural value={filteredTables.length} one="# tabella" other="# tabelle" />
          </p>
          <ul className="flex max-h-[68vh] flex-col overflow-y-auto">
            {filteredTables.map((t) => (
              <SchemaTableRow
                key={t.name}
                table={t}
                selected={t.name === selectedTable}
                onSelect={() => setSelectedTable(t.name)}
              />
            ))}
            {filteredTables.length === 0 ? (
              <li className="rounded-md border border-hairline border-dashed px-3 py-6 text-center text-[12px] text-ink-3">
                <Trans>Nessuna tabella corrisponde a "{debouncedSearch}".</Trans>
              </li>
            ) : null}
          </ul>
        </aside>

        {/* ── Main pane: selected table detail (on cream) ─────────────── */}
        <section className="min-w-0">
          {currentTable ? (
            <SchemaTableDetail key={currentTable.name} connId={connId} table={currentTable} />
          ) : (
            <p className="text-[13px] text-ink-3">
              <Trans>Seleziona una tabella per modificarne la descrizione.</Trans>
            </p>
          )}
        </section>
      </div>
    </div>
  );
};
SchemaViewer.displayName = "SchemaViewer";

// ──────────────────────────────────────────────────────────────────────
// Left rail row
// ──────────────────────────────────────────────────────────────────────

interface SchemaTableRowProps {
  readonly table: TableSchemaResponse;
  readonly selected: boolean;
  readonly onSelect: () => void;
}

const SchemaTableRow: React.FC<SchemaTableRowProps> = ({ table, selected, onSelect }) => (
  <li>
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left transition-colors",
        selected
          ? "bg-cream-deep font-semibold text-ink-1"
          : "font-medium text-ink-2 hover:bg-cream/70 hover:text-ink-1",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
      )}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {isAiDescribed(table.description) ? (
          <Sparkles className="h-3 w-3 shrink-0 text-ando-orange" aria-label="AI" />
        ) : null}
        <span className="truncate text-[13px]">{table.name}</span>
      </span>
      {table.record_count !== null ? (
        <span className="shrink-0 text-[11px] text-ink-4 tabular-nums">
          {table.record_count.toLocaleString("it-IT")}
        </span>
      ) : null}
    </button>
  </li>
);
SchemaTableRow.displayName = "SchemaTableRow";

// ──────────────────────────────────────────────────────────────────────
// Main detail pane
// ──────────────────────────────────────────────────────────────────────

interface SchemaTableDetailProps {
  readonly connId: string;
  readonly table: TableSchemaResponse;
}

const SchemaTableDetail: React.FC<SchemaTableDetailProps> = ({ connId, table }) => {
  const { t } = useLingui();
  const [description, setDescription] = React.useState(table.description);
  const [columnEdits, setColumnEdits] = React.useState<Record<string, string>>({});
  const [showRows, setShowRows] = React.useState(false);
  const mutation = useUpdateSchemaDescription();
  const [error, setError] = React.useState<string | null>(null);
  const [savedAt, setSavedAt] = React.useState<number | null>(null);

  React.useEffect(() => {
    setDescription(table.description);
    setColumnEdits({});
    setShowRows(false);
    setError(null);
    setSavedAt(null);
  }, [table.description, table.name]);

  const isDirty = description !== table.description || Object.keys(columnEdits).length > 0;

  const handleSave = React.useCallback(() => {
    setError(null);
    const columnPayload = Object.keys(columnEdits).length > 0 ? { ...columnEdits } : undefined;
    mutation.mutate(
      {
        connId,
        objectName: table.name,
        body: { description, column_descriptions: columnPayload },
      },
      {
        onSuccess: () => {
          setSavedAt(Date.now());
          setColumnEdits({});
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : t`Salvataggio non riuscito`);
        },
      },
    );
  }, [columnEdits, connId, description, mutation, table.name, t]);

  const descriptionId = React.useId();

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-[16px] text-ink-1">{table.name}</h3>
          <MetaRow className="mt-1.5">
            <MetaItem icon={<Columns3 className="h-3.5 w-3.5" aria-hidden />}>
              <Plural value={table.columns.length} one="# colonna" other="# colonne" />
            </MetaItem>
            {table.record_count !== null ? (
              <MetaItem icon={<Hash className="h-3.5 w-3.5" aria-hidden />}>
                <Plural value={table.record_count} one="# riga" other="# righe" />
              </MetaItem>
            ) : null}
            {table.domain ? (
              <MetaItem icon={<Database className="h-3.5 w-3.5" aria-hidden />}>
                {table.domain}
              </MetaItem>
            ) : null}
          </MetaRow>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowRows((s) => !s)}
            aria-expanded={showRows}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
            <Trans>Anteprima dati</Trans>
          </Button>
          {savedAt !== null ? (
            <span className="text-[11px] text-success">
              <Trans>Salvato</Trans>
            </span>
          ) : null}
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!isDirty || mutation.isPending}
            onClick={handleSave}
          >
            {mutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : null}
            <Trans>Salva</Trans>
          </Button>
        </div>
      </header>

      {/* 20-row live preview of the table (on demand). */}
      {showRows ? <TableRowsPreview connId={connId} objectName={table.name} /> : null}

      {/* Table-level description editor. */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor={descriptionId} className="font-medium text-[12px] text-ink-2">
          <Trans>Descrizione tabella</Trans>
        </label>
        <textarea
          id={descriptionId}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder={t`Descrivi cosa rappresenta questa tabella…`}
          className="w-full resize-y rounded-md border border-hairline bg-transparent px-3 py-2 text-[13px] text-ink-1 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
        />
      </div>

      <InlineErrorBanner message={error} />

      {/* Columns as a dense list on cream. */}
      <div className="flex flex-col gap-1.5">
        <h4 className="font-medium text-[11px] text-ink-3 uppercase tracking-[0.08em]">
          <Trans>Colonne</Trans>
        </h4>
        <ul className="flex flex-col">
          {table.columns.map((col) => (
            <ColumnRow
              key={col.name}
              connId={connId}
              objectName={table.name}
              column={col}
              value={columnEdits[col.name] ?? col.description}
              onChange={(value) =>
                setColumnEdits((prev) => {
                  if (value === col.description) {
                    const { [col.name]: _drop, ...rest } = prev;
                    return rest;
                  }
                  return { ...prev, [col.name]: value };
                })
              }
            />
          ))}
        </ul>
      </div>
    </div>
  );
};
SchemaTableDetail.displayName = "SchemaTableDetail";

// ──────────────────────────────────────────────────────────────────────
// Column row (metadata + description + sample-values peek)
// ──────────────────────────────────────────────────────────────────────

interface ColumnRowProps {
  readonly connId: string;
  readonly objectName: string;
  readonly column: ColumnSchemaResponse;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

const ColumnRow: React.FC<ColumnRowProps> = ({ connId, objectName, column, value, onChange }) => {
  const { t, i18n } = useLingui();
  const id = React.useId();
  const [open, setOpen] = React.useState(false);

  return (
    <li className="border-hairline/60 border-b py-2.5 last:border-b-0">
      <div className="grid grid-cols-1 items-center gap-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto]">
        {/* Name + type + flags */}
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <code className="truncate font-mono text-[13px] text-ink-1">{column.name}</code>
            {column.is_primary_key ? (
              <KeyRound
                className="h-3.5 w-3.5 shrink-0 text-ando-orange"
                aria-label={i18n._(msg`Chiave primaria`)}
              />
            ) : null}
            {column.foreign_key ? (
              <Link2
                className="h-3.5 w-3.5 shrink-0 text-ink-4"
                aria-label={i18n._(msg`Chiave esterna verso ${column.foreign_key}`)}
              />
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-4">
            <span className="font-mono">{column.data_type}</span>
            {column.is_categorical ? (
              <span>
                · <Trans>categorico</Trans>
              </span>
            ) : null}
            {column.cardinality ? <span>· {column.cardinality}</span> : null}
            {!column.nullable ? <span>· NOT NULL</span> : null}
          </div>
        </div>

        {/* Full description editor + the semantic metadata actually seen by Ando. */}
        <div className="min-w-0 space-y-1.5">
          <label htmlFor={id} className="sr-only">
            <Trans>Descrizione colonna {column.name}</Trans>
          </label>
          <textarea
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={2}
            placeholder={t`Descrivi questa colonna…`}
            className="w-full resize-y rounded-md border border-hairline bg-transparent px-2.5 py-1.5 text-[13px] text-ink-1 leading-5 placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
          />
          {column.business_synonyms.length > 0 || column.aggregation_hint ? (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-ink-4">
              {column.business_synonyms.length > 0 ? (
                <span>
                  <Trans>Sinonimi</Trans>: {column.business_synonyms.join(", ")}
                </span>
              ) : null}
              {column.aggregation_hint && column.aggregation_hint !== "none" ? (
                <span>
                  <Trans>Uso</Trans>: {column.aggregation_hint}
                </span>
              ) : null}
            </div>
          ) : null}
          {column.value_descriptions && Object.keys(column.value_descriptions).length > 0 ? (
            <div className="flex flex-wrap gap-1" aria-label={t`Legenda valori`}>
              {Object.entries(column.value_descriptions).map(([code, meaning]) => (
                <span
                  key={code}
                  className="inline-flex items-baseline gap-1 rounded border border-hairline bg-cream/60 px-1.5 py-0.5 text-[10.5px]"
                >
                  <code className="font-mono text-ink-2">{code}</code>
                  <span className="text-ink-4">=</span>
                  <span className="text-ink-3">{meaning}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* Sample-values peek toggle */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className={cn(
            "flex shrink-0 items-center gap-1 rounded-md border px-2 py-1.5 text-[11px] transition-colors",
            open
              ? "border-ando-orange/40 text-ando-orange"
              : "border-hairline text-ink-3 hover:text-ink-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
          )}
        >
          <Eye className="h-3.5 w-3.5" aria-hidden />
          <Trans>Dati</Trans>
        </button>
      </div>

      {open ? <ColumnSamplePanel connId={connId} objectName={objectName} column={column} /> : null}
    </li>
  );
};
ColumnRow.displayName = "ColumnRow";

// ──────────────────────────────────────────────────────────────────────
// Sample-values panel (per column)
// ──────────────────────────────────────────────────────────────────────

const ColumnSamplePanel: React.FC<{
  readonly connId: string;
  readonly objectName: string;
  readonly column: ColumnSchemaResponse;
}> = ({ connId, objectName, column }) => {
  const { t } = useLingui();
  const query = useSampleColumn(connId, objectName, column.name, true);
  // Show stored sample values instantly; swap to fresh live values on arrival.
  const stored = column.sample_values ?? [];
  const live = query.data?.values ?? [];
  const values = live.length > 0 ? live : stored;
  const decoded = column.value_descriptions;
  const hasDecode = !!decoded && Object.keys(decoded).length > 0;
  const undecodedLabel = t`non documentato`;

  return (
    <div className="mt-2 ml-0.5 border-hairline border-l pl-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] text-ink-3 uppercase tracking-[0.06em]">
        <Database className="h-3 w-3" aria-hidden />
        <Trans>Valori di esempio</Trans>
        {query.isFetching ? (
          <Loader2 className="h-3 w-3 animate-spin text-ink-4" aria-hidden />
        ) : null}
      </div>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {values.slice(0, 20).map((v, i) => {
            const { text, isNull } = formatSampleValue(v);
            // ERP enums store opaque integers (Mago: `(tag << 16) | ordinal`),
            // so the code alone tells a reviewer nothing. Show the decoded
            // meaning beside it when the profile carries one, and mark the
            // ones it does not — that gap is what a human needs to spot.
            const meaning = decoded?.[text];
            const undecoded = hasDecode && !isNull && meaning === undefined;
            return (
              <span
                key={`${text}-${i}`}
                className={cn(
                  "inline-flex items-baseline gap-1.5 rounded border border-hairline px-1.5 py-0.5 text-[11px]",
                  isNull ? "text-ink-4 italic" : "text-ink-2",
                  undecoded && "border-dashed",
                )}
                title={meaning ?? (undecoded ? undecodedLabel : undefined)}
              >
                <span className="font-mono">{text}</span>
                {meaning ? (
                  <span className="text-ink-3">{meaning}</span>
                ) : undecoded ? (
                  <span className="text-ink-4 italic">{undecodedLabel}</span>
                ) : null}
              </span>
            );
          })}
        </div>
      ) : query.isFetching ? (
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={`s-${i}`} className="h-5 w-14" />
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-ink-4">
          <Trans>Nessun valore disponibile per questa colonna.</Trans>
        </p>
      )}
    </div>
  );
};
ColumnSamplePanel.displayName = "ColumnSamplePanel";

// ──────────────────────────────────────────────────────────────────────
// Table rows preview (20 live rows)
// ──────────────────────────────────────────────────────────────────────

// Resizable-column preview (SOTA Jul 2026): drag a column border to widen,
// double-click the handle to auto-fit its content, click a cell to copy.
// No heavy table lib — a 20-row preview drives a lightweight ``colgroup`` +
// ``table-layout: fixed`` grid so cells truncate to the (resizable) col width.
const MIN_COL_W = 64;
const DEFAULT_COL_W = 176;
const MAX_COL_W = 640;
const MONO_CHAR_PX = 7.1; // ≈ one 12px monospace glyph
const KEY_RESIZE_STEP = 24;
// Row-preview cells keep the (near-)full value — CSS truncates the display to
// the resizable column width; the tooltip + click-to-copy reveal the rest.
const PREVIEW_MAX_LEN = 4000;

/** Pure numbers → right-align + tabular-nums; dates/text (with ``-``/``:``/space) stay left. */
function isNumericText(t: string): boolean {
  return t.length > 0 && /^-?\d[\d.,]*$/.test(t);
}

type SortState = { readonly col: string; readonly dir: "asc" | "desc" };

/** Numeric-aware comparator for two raw sample values; NULLs always sort last. */
function compareSampleValues(a: unknown, b: unknown): number {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  const as = String(a);
  const bs = String(b);
  const an = Number(as);
  const bn = Number(bs);
  if (as.trim() !== "" && bs.trim() !== "" && !Number.isNaN(an) && !Number.isNaN(bn)) {
    return an - bn;
  }
  return as.localeCompare(bs);
}

const TableRowsPreview: React.FC<{ readonly connId: string; readonly objectName: string }> = ({
  connId,
  objectName,
}) => {
  const { i18n } = useLingui();
  const query = useSampleRows(connId, objectName, true);
  const columns = query.data?.columns ?? [];
  const rows = query.data?.rows ?? [];

  const [widths, setWidths] = React.useState<Record<string, number>>({});
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
  const dragRef = React.useRef<{ col: string; startX: number; startW: number } | null>(null);

  const widthOf = React.useCallback((c: string): number => widths[c] ?? DEFAULT_COL_W, [widths]);
  const setWidth = React.useCallback((c: string, w: number): void => {
    setWidths((prev) => ({
      ...prev,
      [c]: Math.max(MIN_COL_W, Math.min(MAX_COL_W, Math.round(w))),
    }));
  }, []);

  const autoFit = React.useCallback(
    (c: string): void => {
      const maxChars = rows.reduce(
        (m, r) => Math.max(m, formatSampleValue(r[c], PREVIEW_MAX_LEN).text.length),
        c.length,
      );
      setWidth(c, maxChars * MONO_CHAR_PX + 30);
    },
    [rows, setWidth],
  );

  const onHandleDown =
    (c: string) =>
    (e: React.PointerEvent<HTMLSpanElement>): void => {
      e.preventDefault();
      dragRef.current = { col: c, startX: e.clientX, startW: widthOf(c) };
      e.currentTarget.setPointerCapture(e.pointerId);
    };
  const onHandleMove = (e: React.PointerEvent<HTMLSpanElement>): void => {
    const d = dragRef.current;
    if (d) setWidth(d.col, d.startW + (e.clientX - d.startX));
  };
  const onHandleUp = (e: React.PointerEvent<HTMLSpanElement>): void => {
    if (dragRef.current) e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
  };
  const onHandleKey =
    (c: string) =>
    (e: React.KeyboardEvent): void => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setWidth(c, widthOf(c) - KEY_RESIZE_STEP);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setWidth(c, widthOf(c) + KEY_RESIZE_STEP);
      }
    };

  const copyCell = (val: string, key: string): void => {
    void navigator.clipboard
      ?.writeText(val)
      .then(() => {
        setCopiedKey(key);
        window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1100);
      })
      .catch(() => undefined);
  };

  // Client-side sort of the 20 preview rows. 3-state per column:
  // unsorted → asc → desc → unsorted (restores the DB's natural order).
  const [sort, setSort] = React.useState<SortState | null>(null);
  const toggleSort = (c: string): void => {
    setSort((prev) => {
      if (prev?.col !== c) return { col: c, dir: "asc" };
      if (prev.dir === "asc") return { col: c, dir: "desc" };
      return null;
    });
  };
  const sortedRows = React.useMemo(() => {
    if (!sort) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => factor * compareSampleValues(a[sort.col], b[sort.col]));
  }, [rows, sort]);

  const totalWidth = columns.reduce((s, c) => s + widthOf(c), 0);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 text-[10.5px] text-ink-3 uppercase tracking-[0.08em]">
        <Eye className="h-3 w-3" aria-hidden />
        <Trans>Anteprima · prime 20 righe</Trans>
        {query.isFetching ? (
          <Loader2 className="h-3 w-3 animate-spin text-ink-4" aria-hidden />
        ) : null}
      </div>
      {query.isError ? (
        <p className="rounded-md border border-hairline border-dashed px-3 py-4 text-[12px] text-ink-3">
          <Trans>Impossibile caricare l'anteprima dei dati.</Trans>
        </p>
      ) : rows.length > 0 ? (
        <>
          <div className="max-h-[60vh] overflow-auto rounded-lg border border-hairline">
            <table
              className="border-collapse text-[12px]"
              style={{ width: totalWidth, tableLayout: "fixed" }}
            >
              <colgroup>
                {columns.map((c) => (
                  <col key={c} style={{ width: widthOf(c) }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {columns.map((c) => {
                    const sorted = sort?.col === c;
                    return (
                      <th
                        key={c}
                        aria-sort={
                          sorted ? (sort.dir === "asc" ? "ascending" : "descending") : "none"
                        }
                        className="sticky top-0 z-10 border-hairline/60 border-b bg-cream px-2.5 py-1.5 text-left font-medium text-ink-3"
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort(c)}
                          aria-label={i18n._(msg`Ordina per ${c}`)}
                          className="flex w-full items-center gap-1 truncate pr-1 text-left transition-colors hover:text-ink-1 focus-visible:text-ink-1 focus-visible:outline-none"
                        >
                          <span className="truncate">{c}</span>
                          {sorted ? (
                            sort.dir === "asc" ? (
                              <ArrowUp className="h-3 w-3 shrink-0 text-ando-orange" aria-hidden />
                            ) : (
                              <ArrowDown
                                className="h-3 w-3 shrink-0 text-ando-orange"
                                aria-hidden
                              />
                            )
                          ) : null}
                        </button>
                        <span
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={i18n._(msg`Ridimensiona colonna ${c}`)}
                          tabIndex={0}
                          onPointerDown={onHandleDown(c)}
                          onPointerMove={onHandleMove}
                          onPointerUp={onHandleUp}
                          onDoubleClick={() => autoFit(c)}
                          onKeyDown={onHandleKey(c)}
                          className="absolute inset-y-0 right-0 w-1.5 cursor-col-resize touch-none bg-transparent transition-colors hover:bg-ando-orange/60 focus-visible:bg-ando-orange focus-visible:outline-none"
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, ri) => (
                  <tr
                    key={`r-${ri}`}
                    className="border-hairline/40 border-b transition-colors last:border-b-0 hover:bg-black/[0.015]"
                  >
                    {columns.map((c) => {
                      const { text, isNull } = formatSampleValue(row[c], PREVIEW_MAX_LEN);
                      const cellKey = `${ri}:${c}`;
                      const copyable = !isNull && text.length > 0;
                      return (
                        <td
                          key={c}
                          title={isNull ? undefined : text}
                          onClick={copyable ? () => copyCell(text, cellKey) : undefined}
                          className={cn(
                            "relative truncate px-2.5 py-1.5 font-mono",
                            !isNull && isNumericText(text)
                              ? "text-right tabular-nums"
                              : "text-left",
                            isNull ? "text-ink-4 italic" : "text-ink-1",
                            copyable ? "cursor-copy" : "",
                          )}
                        >
                          {text}
                          {copiedKey === cellKey ? (
                            <Check
                              className="-translate-y-1/2 absolute top-1/2 right-1 h-3 w-3 text-ando-orange"
                              aria-hidden
                            />
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10.5px] text-ink-4">
            <Trans>
              Trascina il bordo di una colonna per allargarla · doppio clic per adattarla · clic su
              una cella per copiare
            </Trans>
          </p>
        </>
      ) : query.isFetching ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <p className="rounded-md border border-hairline border-dashed px-3 py-4 text-[12px] text-ink-3">
          <Trans>La tabella non contiene righe da mostrare.</Trans>
        </p>
      )}
    </div>
  );
};
TableRowsPreview.displayName = "TableRowsPreview";

// ──────────────────────────────────────────────────────────────────────
// Empty / loading
// ──────────────────────────────────────────────────────────────────────

const SchemaSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_minmax(0,1fr)]" aria-busy>
    <div className="flex flex-col gap-2">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-7 w-full" />
      <Skeleton className="h-7 w-full" />
      <Skeleton className="h-7 w-full" />
      <Skeleton className="h-7 w-full" />
    </div>
    <div className="flex flex-col gap-3">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  </div>
);
SchemaSkeleton.displayName = "SchemaSkeleton";

const SchemaEmptyState: React.FC<{ onStartDiscovery?: () => void }> = ({ onStartDiscovery }) => (
  <div className="flex flex-col items-center gap-3 rounded-xl border border-hairline border-dashed px-6 py-10 text-center">
    <p className="font-semibold text-[14px] text-ink-1">
      <Trans>Nessuno schema scoperto.</Trans>
    </p>
    <p className="max-w-md text-[13px] text-ink-3 leading-relaxed">
      <Trans>Avvia la scoperta dal pulsante in alto per analizzare le tabelle disponibili.</Trans>
    </p>
    {onStartDiscovery ? (
      <Button type="button" variant="primary" size="sm" onClick={onStartDiscovery}>
        <Trans>Avvia scoperta schema</Trans>
      </Button>
    ) : null}
  </div>
);
SchemaEmptyState.displayName = "SchemaEmptyState";

const SchemaSyncInProgress: React.FC = () => (
  <output
    className="flex flex-col items-center gap-3 rounded-xl border border-hairline border-dashed px-6 py-10 text-center"
    aria-live="polite"
  >
    <Loader2 className="h-5 w-5 animate-spin text-ando-orange" aria-hidden />
    <p className="font-semibold text-[14px] text-ink-1">
      <Trans>Sincronizzazione schema in corso…</Trans>
    </p>
    <p className="max-w-md text-[13px] text-ink-3 leading-relaxed">
      <Trans>
        Stiamo analizzando le tabelle. Lo schema apparirà qui appena pronto, senza bisogno di
        ricaricare la pagina.
      </Trans>
    </p>
  </output>
);
SchemaSyncInProgress.displayName = "SchemaSyncInProgress";
